import moment from "moment";

import { PredefinedRuleSet, PredefinedRuleSetItem } from "@/Appointments/Api/Dto/Responses/PredefinedRuleSet";
import { SchedulerHelper } from "@/Appointments/Helpers/SchedulerHelper";
import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { CalendarSlotV2, WorkDayV2 } from "@/Appointments/Models/Scheduling/WorkDayV2";
import { SchedulingStore } from "@/Appointments/Stores/SchedulingStore";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ServiceDefinitionModel } from "@/Common/Models/Services/ServiceDefinitionModel";
import { CalendarSlotType, SchedulingTabViews } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class CreateTemplateTabHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.CreateTemplateTab.CancelClick, this.cancelClick);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnNameChange, this.onNameChange);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnHourStartChange, this.onHourStartChange);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnHourEndChange, this.onHourEndChange);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnSiteChange, this.onSiteChange);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.AddPredefinedTemplate, this.addPredefinedTemplate);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.SavePredefinedTemplate, this.savePredefinedTemplate);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.SaveEditionPredefinedTemplate, this.saveEditionPredefinedTemplate);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.ChangeTypeCode, this.changeTypeCode);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OpenAddServicePopup, this.openAddServicePopup);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.ChangeComboServiceSelection, this.changeComboServiceSelection);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.AddRuleSetService, this.addRuleSetService);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.PrepareRuleSetSave, this.prepareRuleSetSave);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnDeleteClick, this.onDeleteClick);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.OnDeleteRuleSetClick, this.onDeleteRuleSetClick);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.ClearModel, this.clearModel);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.DeleteItemFromNewTemplate, this.deleteItemFromNewTemplate);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.ClearCurrentRulesetInfo, this.clearCurrentRulesetInfo);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.UpdateSelectedComboServicesOnChanges, this.updateSelectedComboServicesOnChanges);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.StartOver, this.onStartOverClick);
        this.RegisterOperation(LocalOperations.CreateTemplateTab.SelectExistingRuleset, this.selectExistingRuleset);
    }

    private cancelClick(request: LocalEvent, store: SchedulingStore): void {
        store.SchedulingTabModel = store.SchedulingTabModel.set("CurrentView", SchedulingTabViews.MainTemplate);

        store.CreateTemplateTabModel = Immutable.fromJS(SchedulerHelper.getDefaultCreateTemplateTabModel());
    }

    private onNameChange(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("Name", request.Get<string>("value"));
    }

    private onHourStartChange(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("HourStart", request.Get<Date>("value"));
    }

    private onHourEndChange(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("HourEnd", request.Get<Date>("value"));
    }

    private onSiteChange(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("SelectedSiteId", request.Get<string>("value"));
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("InitialSelectedServiceComboList", [].toImmutable());
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", [].toImmutable());
    }

    private addPredefinedTemplate(request: LocalEvent, store: SchedulingStore): void {
        let selectedService = request.Get<ServiceDefinitionModel>("service");

        let startTime = store.CreateTemplateTabModel.get("HourStart");
        let endTime = store.CreateTemplateTabModel.get("HourEnd");

        let totalMinute = (Math.abs(endTime.getTime() - startTime.getTime()) / 36e5) * 60;
        let nbSolts = totalMinute / selectedService.GetDefaultDurationMinutes();

        let workDays: WorkDayV2[] = [];
        let workDay: WorkDayV2;

        if (store.CreateTemplateTabModel.get("WorkDayList").size === 0) {
            workDay = new WorkDayV2();
            workDay.date = DateTime.nowDeprecated().toMoment().startOf("day").toDate();
            workDay.calendarSlots = [];
            workDay.HasRuleSet = false;
        }
        else {
            workDay = store.CreateTemplateTabModel.get("WorkDayList").toArray()[0];
        }

        for (let n = 0; n < nbSolts; n++) {
            let calendarSlot = new CalendarSlotV2();
            calendarSlot.id = GuidHelper.NewGuid();

            calendarSlot.fromRaw = startTime.addMinutes((selectedService.GetDefaultDurationMinutes() * n));
            calendarSlot.toRAW = startTime.addMinutes((selectedService.GetDefaultDurationMinutes() * (n + 1)));
            calendarSlot.Model = selectedService;
            calendarSlot.Type = CalendarSlotType.PredefinedAppointment;

            if (!(calendarSlot.toRAW > endTime))
                workDay.calendarSlots.push(calendarSlot);
        }

        workDay.calendarSlots = workDay.calendarSlots.sortByProperty(n => n.fromRaw);
        workDays.push(workDay);

        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("WorkDayList", workDays.toImmutable());
    }

    private savePredefinedTemplate(request: LocalEvent, store: SchedulingStore): void {
        store.schedulingAction.savePredefinedTemplate(store.CreateTemplateTabModel);
    }

    private saveEditionPredefinedTemplate(request: LocalEvent, store: SchedulingStore): void {
        store.schedulingAction.updatePredefinedTemplate(store.CreateTemplateTabModel);
    }

    private changeTypeCode(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("CreateScheduleTypeCode", request.Get<string>("type"));
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", [].toImmutable());
    }

    private openAddServicePopup(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("OpenAddServicePopup", !store.CreateTemplateTabModel.get("OpenAddServicePopup"));

        if (store.CreateTemplateTabModel.get("OpenAddServicePopup") === true) {
            sessionStore.showGlobalOverlay(true);
        }
        else {
            sessionStore.hideGlobalOverlay();
        }
    }

    private changeComboServiceSelection(request: LocalEvent, store: SchedulingStore): void {
        let selected = request.Get<Immutable.List<ServiceDefinitionWithItsRelatedCombos>>("selectedItems");
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", selected);
    }

    private addRuleSetService(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("OpenAddServicePopup", false);
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("InitialSelectedServiceComboList", store.CreateTemplateTabModel.get("ComboServiceList"));

        sessionStore.hideGlobalOverlay();
    }

    private prepareRuleSetSave(request: LocalEvent, store: SchedulingStore): void {
        let startTime = store.CreateTemplateTabModel.get("HourStart");
        let endTime = store.CreateTemplateTabModel.get("HourEnd");

        let workDays: WorkDayV2[] = [];
        let workDay: WorkDayV2;

        if (store.CreateTemplateTabModel.get("WorkDayList").size === 0) {
            workDay = new WorkDayV2();
            workDay.date = DateTime.nowDeprecated().toMoment().startOf("day").toDate();
            workDay.calendarSlots = [];
            workDay.HasRuleSet = true;
        }
        else {
            workDay = store.CreateTemplateTabModel.get("WorkDayList").toArray()[0];
        }
        let comboList = this.transformServiceComboToRuleSet(store.CreateTemplateTabModel.get("ComboServiceList").toArray(), startTime, endTime);

        let calendarSlot = CalendarSlotV2.FromPredefinedRuleSet(comboList);
        calendarSlot.fromRaw.setFullYear(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate());
        calendarSlot.toRAW.setFullYear(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate());

        if (!workDay.calendarSlots.some(u => u.fromRaw.valueOf() < calendarSlot.toRAW.valueOf() && u.toRAW.valueOf() > calendarSlot.fromRaw.valueOf() && calendarSlot.Type === CalendarSlotType.RuleSet)) {
            workDay.calendarSlots.push(calendarSlot);
        }
        else {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: cacheStore.GetString("RuleSetClashLabel"),
                showCancel: true
            });
        }
        workDays.push(workDay);

        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("WorkDayList", workDays.toImmutable());
    }

    private transformServiceComboToRuleSet(serviceComboList: ServiceDefinitionWithItsRelatedCombos[], startTime: Date, endTime: Date): PredefinedRuleSet {
        let rs: PredefinedRuleSet = new PredefinedRuleSet();

        rs.StartDate = startTime.toString();
        rs.EndDate = endTime.toString();

        rs.PredefinedRuleSetItems = [];

        let i = 0;
        serviceComboList.forEach(item => {
            if (item.RelatedServiceDefinitionContexts != null) {
                item.RelatedServiceDefinitionContexts.forEach(prsi => {
                    let rsi: PredefinedRuleSetItem = new PredefinedRuleSetItem();
                    let service = cacheStore.GetServiceDefinitionById(item.ServiceDefinitionId);

                    rsi.ID = i.toString();
                    rsi.TargetedClient = prsi.TargetedClient;
                    rsi.ServiceDefinitionId = item.ServiceDefinitionId;
                    rsi.ComboId = prsi.ComboId == null ? null : prsi.ComboId.toString();
                    rsi.GroupMax = item.GroupMax ? item.GroupMax : 0;
                    rsi.Max = prsi.Max ? prsi.Max : 0;
                    rsi.Min = prsi.Min ? prsi.Min : 0;
                    rsi.DurationMinutes = item.ServiceDefinitionDuration / 600000000;
                    rsi.ServiceNameLookupId = service.Model.NameLookupId;
                    rsi.IsOnlineBookingAllowed = prsi.IsOnline;
                    rsi.IsTeleconsultation = prsi.IsTeleconsultation;

                    rs.SiteId = service.Model.SiteId;
                    rs.PredefinedRuleSetItems.push(rsi);
                    i++;
                });
            }
        });

        return rs;
    }

    private onDeleteClick(request: LocalEvent, store: SchedulingStore): void {
        let startTime = store.CreateTemplateTabModel.get("HourStart");
        let endTime = store.CreateTemplateTabModel.get("HourEnd");
        let workDay = store.CreateTemplateTabModel.get("WorkDayList").toArray();

        // Work day will always be alone in his array.
        workDay[0].calendarSlots = workDay[0].calendarSlots.filter(u => !(u.fromRaw.valueOf() >= startTime.valueOf() && u.toRAW.valueOf() <= endTime.valueOf()));
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("WorkDayList", workDay.toImmutable());
    }

    private onDeleteRuleSetClick(request: LocalEvent, store: SchedulingStore): void {
        let startTime = store.CreateTemplateTabModel.get("HourStart");
        let endTime = store.CreateTemplateTabModel.get("HourEnd");
        let workDay = store.CreateTemplateTabModel.get("WorkDayList").toArray();

        // Work day will always be alone in his array.
        workDay[0].calendarSlots = workDay[0].calendarSlots.filter(u => u.fromRaw.valueOf() != startTime.valueOf() && u.toRAW.valueOf() != endTime.valueOf());
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("WorkDayList", workDay.toImmutable());
    }

    private clearModel(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = Immutable.fromJS(SchedulerHelper.getDefaultCreateTemplateTabModel());
    }

    private deleteItemFromNewTemplate(request: LocalEvent, store: SchedulingStore): void {
        let item = request.Get<CalendarSlotV2>("item");
        let workDay = store.CreateTemplateTabModel.get("WorkDayList").toArray();

        workDay[0].calendarSlots = workDay[0].calendarSlots.filter(u => u.id != item.id);
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("WorkDayList", workDay.toImmutable());
    }

    private clearCurrentRulesetInfo(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", [].toImmutable());
    }

    private updateSelectedComboServicesOnChanges(request: LocalEvent, store: SchedulingStore): void {
        let selectedComboServiceList = request.Get<Immutable.List<ServiceDefinitionWithItsRelatedCombos>>("item");
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", selectedComboServiceList);
    }

    private onStartOverClick(request: LocalEvent, store: SchedulingStore): void {
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("ComboServiceList", [].toImmutable());
        store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("InitialSelectedServiceComboList", [].toImmutable());
        this.openAddServicePopup(request, store);
    }

    private selectExistingRuleset(request: LocalEvent, store: SchedulingStore): void {
        let selection = request.Get<CalendarSlotV2>("value");
        if (selection != null) {
            let ruleset = selection.Model as PredefinedRuleSet;
            if (ruleset != null) {
                let rulesetModel = PredefinedRuleSetModel.New();
                rulesetModel.Model = ruleset;
                rulesetModel.Model.DateStartDate = moment(ruleset.StartDate).toDate();
                rulesetModel.Model.DateEndDate = moment(ruleset.EndDate).toDate();
                let comboServiceList = rulesetModel.ToComboServiceList();

                store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("CreateScheduleTypeCode", "1"); // TypeCode 1 is scheduling rule
                store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("HourStart", rulesetModel.Model.DateStartDate);
                store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("HourEnd", rulesetModel.Model.DateEndDate);
                store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("SelectedSiteId", rulesetModel.Model.SiteId);
                store.CreateTemplateTabModel = store.CreateTemplateTabModel.set("InitialSelectedServiceComboList", comboServiceList.toImmutable());
            }
        }
    }
}

export { CreateTemplateTabHandler };
